import {
  HStack,
  Image,
  Text,
  Stack,
  Divider,
  Input,
  ListItem,
  UnorderedList,
} from "@chakra-ui/react";
import React, { useRef } from "react";
import { sprintf } from "sprintf-js";
import type { OrderProductData } from "@/store/types/order";
import { renderInnerHtml } from "@/helpers/dom";
import { observer } from "mobx-react";
import { telemetry } from "@/telemetry";

interface Props {
  product: OrderProductData;
  subtotalLabel: string;
  onQuantityChange: (quantity: number) => unknown;
  showSubtotal?: boolean;
}

export const OrderUnitItem = observer(
  ({
    product,
    onQuantityChange,
    subtotalLabel,
    showSubtotal = true,
  }: Props) => {
    const { language } = product;
    const quantityOnFocus = useRef(product.quantity);

    const handleChange = (value: string) => {
      onQuantityChange(value ? parseInt(value, 10) : 0);
    };

    const handleFocus = () => {
      quantityOnFocus.current = product.quantity;
    };

    const handleBlur = () => {
      if (product.quantity === quantityOnFocus.current) return;

      telemetry.trackEvent("product_quantity_set", {
        product_id: String(product.id),
        quantity: product.quantity,
        below_minimum:
          product.quantity > 0 && product.quantity < product.minimumQuantity,
      });
    };

    return (
      <HStack
        id="form"
        spacing="10"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Image
          src={language.imageSrc}
          alt="unit image"
          height="292px"
          minWidth={{ base: "100%", sm: "340px" }}
          alignSelf="flex-start"
          borderRadius="6px"
          objectFit="cover"
        />
        <Stack
          spacing="5"
          width="100%"
          alignSelf="flex-start"
        >
          <Stack spacing="0">
            <Text>{language.itemsDescLabel}</Text>
            <UnorderedList pl="4">
              {language.items.map((item, index) => (
                <ListItem key={index}>{renderInnerHtml(item)}</ListItem>
              ))}
            </UnorderedList>
          </Stack>
          <HStack>
            <Text>{language.pricePerLabel}</Text>
            <Text
              fontWeight="bold"
              color="primary-light"
            >
              {product.priceFormatted}
            </Text>
          </HStack>
          <Divider />
          <HStack
            justifyContent="space-between"
            textAlign="center"
          >
            <Stack maxW="200px">
              <Text>{language.numberOfLabel}</Text>
              <Input
                size="sm"
                onChange={(e) => handleChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={product.quantity}
              />
              <Text
                fontSize="xs"
                color="secondary"
              >
                {renderInnerHtml(
                  sprintf(
                    product.language.minimumOrder,
                    product.minimumQuantity,
                  ),
                )}
              </Text>
            </Stack>
            {showSubtotal && (
              <Stack textAlign="end">
                <Text>{subtotalLabel}</Text>
                <Text
                  color="primary-light"
                  fontWeight="bold"
                >
                  {product.quantity >= product.minimumQuantity
                    ? product.totalFormatted
                    : "-"}
                </Text>
              </Stack>
            )}
          </HStack>
        </Stack>
      </HStack>
    );
  },
);
